/**
 * Plants on the ground, and the clock that brings them back (M3-03).
 *
 * Flora is **sparse and tile-attached**: a record per patch, keyed by its
 * coordinate, rather than a layer the width of the map. A 256×256 second tile
 * array would be 65,536 numbers cloned every tick to describe a few thousand
 * plants, and the whole valley is mostly not a herb.
 *
 * The design claim this defends is scarcity that is *felt but gentle*
 * (M3-03): a meadow somebody has stripped stays stripped for days, so
 * over-foraging is a thing you did to a place rather than a cooldown you
 * waited out — but eight players cannot permanently exhaust the valley,
 * because every patch comes back on its own.
 *
 * Regrowth is a **deadline, not a countdown**, for the third time in this
 * codebase (see `ecology.ts` and `wound.ts`): a number that is simply true or
 * not survives a server restart that skipped an hour, and costs nothing on the
 * ticks where nothing happens.
 */
/** The key a patch lives under. Position *is* its identity — one patch a tile. */
export function floraKey(x, y) {
    return `${x},${y}`;
}
export function createFlora(x, y, speciesId, picks) {
    return { x, y, speciesId, remaining: picks, regrowAt: 0 };
}
/** Is there anything here worth stopping for? */
export function canForage(patch) {
    return patch !== undefined && patch.remaining > 0;
}
/**
 * Bring patches back, one pick at a time.
 *
 * Called on the hour rather than the tick, for the reason `reconcileDen` is:
 * a plant is a slow thing, and asking ten times a second whether the yarrow
 * has grown is ten times a second spent on a question whose answer changes
 * about once a day.
 *
 * One pick per interval rather than a full refill, so a stripped meadow
 * recovers *gradually*. A patch that sprang back to full the moment its timer
 * expired would make over-foraging free again — you would simply come back
 * the next morning to the same meadow you found on the first day.
 */
export function regrow(patch, def, tick) {
    if (patch.remaining >= def.picks)
        return false;
    if (tick < patch.regrowAt)
        return false;
    patch.remaining += 1;
    patch.regrowAt = tick + def.regrowTicks;
    return true;
}
/**
 * Record a pick, and set the clock.
 *
 * The clock is only *extended*, never shortened: picking the last of a patch
 * that was already recovering must not reset it to a shorter wait than it was
 * already serving.
 */
export function take(patch, def, tick) {
    patch.remaining = Math.max(0, patch.remaining - 1);
    patch.regrowAt = Math.max(patch.regrowAt, tick + def.regrowTicks);
}
/**
 * How full a patch looks, for the examine line. Named stages again — a player
 * reads "picked over", never "1/3".
 */
export function standingOf(patch, def) {
    if (patch.remaining >= def.picks)
        return 'thick';
    if (patch.remaining > 0)
        return 'thin';
    return 'picked-over';
}
//# sourceMappingURL=flora.js.map